import { tryAssetIdLikeToLegacyAssetId } from "@repo/caip";
import type { RwaTokenSubjectRelationType } from "@repo/ports/v2";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";
import type {
  AssetUniverseCanonicalModel,
  AssetUniverseProductModel,
  AssetUniverseRouteModel,
  AssetUniverseSymbolLinkModel,
} from "./db/models";
import {
  normalizeAssetDomainAssetIds,
  toAssetUniverseCanonicalId,
  toAssetUniverseProductId,
  toAssetUniverseProductRouteId,
  toAssetUniverseSymbolLinkId,
  toAssetUniverseTokenRouteId,
} from "./db/models";

export type AssetUniverseListOutput = Awaited<
  ReturnType<
    PlatformApiTrpcClient["assetTaxonomy"]["listAssetUniverse"]["query"]
  >
>;
export type AssetUniverseSearchOutput = Awaited<
  ReturnType<
    PlatformApiTrpcClient["assetTaxonomy"]["searchAssetUniverse"]["query"]
  >
>;
export type AssetUniverseCanonicalGroup =
  AssetUniverseListOutput["items"][number];
export type AssetUniverseSummaryListOutput = Awaited<
  ReturnType<
    PlatformApiTrpcClient["assetHydration"]["listCanonicalSummaries"]["query"]
  >
>;
export type AssetUniverseCanonicalSummary =
  AssetUniverseSummaryListOutput["items"][number];
export type AssetUniverseCanonicalSummaryLike =
  AssetUniverseCanonicalGroup["underlying"] & {
    readonly imageUrl?: string | null;
  };

export interface NormalizedAssetUniverseGraph {
  assetUniverseCanonical: AssetUniverseCanonicalModel[];
  assetUniverseProducts: AssetUniverseProductModel[];
  assetUniverseRoutes: AssetUniverseRouteModel[];
  assetUniverseSymbolLinks: AssetUniverseSymbolLinkModel[];
}

export interface NormalizedAssetUniverseSummaries {
  assetUniverseCanonical: AssetUniverseCanonicalModel[];
  assetUniverseSymbolLinks: AssetUniverseSymbolLinkModel[];
}

const TOKEN_SUBJECT_RELATION_TYPES = new Set<RwaTokenSubjectRelationType>([
  "native_asset",
  "primary_token",
  "wrapped_token",
  "bridged_token",
  "issuer_representation",
  "receipt_token",
  "staking_derivative",
  "synthetic_token",
]);

export function normalizeAssetUniverseGroups(input: {
  groups: readonly AssetUniverseCanonicalGroup[];
  updatedAt: number;
}): NormalizedAssetUniverseGraph {
  const canonicalRows = new Map<string, AssetUniverseCanonicalModel>();
  const productRows = new Map<string, AssetUniverseProductModel>();
  const routeRows = new Map<string, AssetUniverseRouteModel>();
  const symbolLinkRows = new Map<string, AssetUniverseSymbolLinkModel>();

  function upsertSymbolLink(link: {
    assetCanonicalId: string;
    assetClass: string | null;
    source: AssetUniverseSymbolLinkModel["source"];
    symbol: string | null | undefined;
  }) {
    const symbol = normalizeSymbol(link.symbol);
    if (!symbol) {
      return;
    }

    const id = toAssetUniverseSymbolLinkId(symbol, link.assetClass);
    const existing = symbolLinkRows.get(id);
    if (
      existing &&
      symbolLinkSourceRank(existing.source) <= symbolLinkSourceRank(link.source)
    ) {
      return;
    }

    symbolLinkRows.set(id, {
      assetCanonicalId: link.assetCanonicalId,
      assetClass: link.assetClass,
      id,
      source: link.source,
      symbol,
      updatedAt: input.updatedAt,
    });
  }

  for (const group of input.groups) {
    const assetCanonicalId = group.underlying.assetCanonicalId.trim();
    if (assetCanonicalId.length === 0) {
      continue;
    }

    const coingeckoId = readAssetUniverseGroupCoingeckoId(group);
    const canonicalId = toAssetUniverseCanonicalId(assetCanonicalId);

    canonicalRows.set(
      canonicalId,
      normalizeAssetUniverseCanonicalSummary({
        coingeckoId,
        summary: group.underlying,
        updatedAt: input.updatedAt,
      }),
    );

    upsertSymbolLink({
      assetCanonicalId,
      assetClass: group.underlying.assetClass,
      source: "canonical",
      symbol: group.underlying.canonicalSymbol,
    });

    for (const product of group.issuedProducts) {
      const productId = toAssetUniverseProductId(product.id);
      const routeAssetIds: string[] = [];

      for (const asset of product.assets) {
        const assetId = normalizeAssetUniverseAssetId(asset.assetId);
        if (!assetId) {
          continue;
        }
        routeAssetIds.push(assetId);

        const routeId = toAssetUniverseProductRouteId({
          assetId,
          productId: product.id,
        });
        routeRows.set(routeId, {
          assetDbId: asset.assetDbId,
          assetCanonicalId,
          assetId,
          assetReference: asset.assetReference,
          bebopRfqCanBuy: asset.bebopRfq?.canBuy === true,
          bebopRfqCanSell: asset.bebopRfq?.canSell === true,
          bebopRfqNetwork: asset.bebopRfq?.network ?? null,
          bebopRfqReason: asset.bebopRfq?.reason ?? null,
          chainNamespace: asset.chainNamespace,
          chainReference: asset.chainReference,
          decimals: asset.decimals,
          id: routeId,
          isBebopRfqSupported: asset.bebopRfq?.supported === true,
          isGliderSupported:
            asset.gliderSupported === true ||
            asset.bebopRfq?.supported === true,
          productId: product.id,
          relationshipType: normalizeRelationshipType(product.relationshipType),
          role: asset.role,
          source: product.source,
          symbol: asset.symbol,
          updatedAt: input.updatedAt,
        });
      }

      productRows.set(productId, {
        assetCanonicalId,
        bebopRfqNetworks: [...(product.bebopRfqNetworks ?? [])],
        displayName:
          nonEmptyString(product.displayName) ??
          product.issuerSymbol ??
          group.underlying.name ??
          group.underlying.canonicalSymbol,
        id: productId,
        imageUrl: product.imageUrl,
        isBebopRfqSupported: product.bebopRfqSupported === true,
        isGliderSupported: product.gliderSupported,
        issuerName: product.issuer.name,
        issuerSlug: product.issuer.slug,
        issuerSymbol: product.issuerSymbol,
        metadata: product.metadata,
        priceChangePercent24h: parseOptionalNumber(
          product.priceChangePercent24h,
        ),
        priceUsd: parseOptionalNumber(product.priceUsd),
        productId: product.id,
        productSlug: product.productSlug,
        productType: product.productType,
        relationshipType: normalizeRelationshipType(product.relationshipType),
        routeAssetIds: [...new Set(routeAssetIds)].sort(),
        routeCount: routeAssetIds.length,
        source: product.source,
        sourceProductId: product.sourceProductId,
        tradingStatus: product.tradingStatus,
        updatedAt: input.updatedAt,
      });

      upsertSymbolLink({
        assetCanonicalId,
        assetClass: group.underlying.assetClass,
        source: "product",
        symbol: product.issuerSymbol,
      });
    }

    for (const token of group.tokenRepresentations) {
      const assetId = normalizeAssetUniverseAssetId(token.assetId);
      if (!assetId) {
        continue;
      }

      const relationshipType = normalizeRelationshipType(
        token.relationshipType,
      );
      const routeId = toAssetUniverseTokenRouteId({
        assetCanonicalId,
        assetId,
        relationshipType,
      });
      routeRows.set(routeId, {
        assetDbId: token.assetDbId,
        assetCanonicalId,
        assetId,
        assetReference: token.assetReference,
        bebopRfqCanBuy: false,
        bebopRfqCanSell: false,
        bebopRfqNetwork: null,
        bebopRfqReason: null,
        chainNamespace: token.chainNamespace,
        chainReference: token.chainReference,
        decimals: token.decimals,
        id: routeId,
        isBebopRfqSupported: false,
        isGliderSupported: token.gliderSupported,
        productId: null,
        relationshipType,
        role: null,
        source: token.source,
        symbol: token.symbol,
        updatedAt: input.updatedAt,
      });

      upsertSymbolLink({
        assetCanonicalId,
        assetClass: group.underlying.assetClass,
        source: "route",
        symbol: token.symbol,
      });
    }
  }

  return {
    assetUniverseCanonical: [...canonicalRows.values()],
    assetUniverseProducts: [...productRows.values()],
    assetUniverseRoutes: [...routeRows.values()],
    assetUniverseSymbolLinks: [...symbolLinkRows.values()],
  };
}

export function normalizeAssetUniverseSummaries(input: {
  summaries: readonly AssetUniverseCanonicalSummaryLike[];
  updatedAt: number;
}): NormalizedAssetUniverseSummaries {
  const canonicalRows = new Map<string, AssetUniverseCanonicalModel>();
  const symbolLinkRows = new Map<string, AssetUniverseSymbolLinkModel>();

  for (const summary of input.summaries) {
    const assetCanonicalId = summary.assetCanonicalId.trim();
    const symbol = normalizeSymbol(summary.canonicalSymbol);
    if (!assetCanonicalId || !symbol) {
      continue;
    }

    const canonical = normalizeAssetUniverseCanonicalSummary({
      coingeckoId: readAssetUniverseSummaryCoingeckoId(summary),
      summary,
      updatedAt: input.updatedAt,
    });
    canonicalRows.set(canonical.id, canonical);

    const symbolLinkId = toAssetUniverseSymbolLinkId(
      symbol,
      summary.assetClass,
    );
    symbolLinkRows.set(symbolLinkId, {
      assetCanonicalId,
      assetClass: summary.assetClass,
      id: symbolLinkId,
      source: "canonical",
      symbol,
      updatedAt: input.updatedAt,
    });
  }

  return {
    assetUniverseCanonical: [...canonicalRows.values()],
    assetUniverseSymbolLinks: [...symbolLinkRows.values()],
  };
}

function normalizeAssetUniverseCanonicalSummary(input: {
  coingeckoId: string | null;
  summary: AssetUniverseCanonicalSummaryLike;
  updatedAt: number;
}): AssetUniverseCanonicalModel {
  const canonicalSymbol = normalizeSymbol(input.summary.canonicalSymbol);
  const parsedPriceUsd = parseOptionalNumber(input.summary.priceUsd);
  const parsedPriceChangePercent24h = parseOptionalNumber(
    input.summary.priceChangePercent24h,
  );
  const isUnpricedTradFiPlaceholder =
    isEquityOrFundAssetClass(input.summary.assetClass) &&
    (parsedPriceUsd === null || parsedPriceUsd <= 0);
  return {
    assetCanonicalId: input.summary.assetCanonicalId.trim(),
    assetClass: input.summary.assetClass,
    coingeckoId: input.coingeckoId,
    countryCode: input.summary.countryCode,
    currencyCode: input.summary.currencyCode,
    derivativeMarketCount: input.summary.derivativeMarketCount,
    gliderSupported: input.summary.gliderSupported,
    id: toAssetUniverseCanonicalId(input.summary.assetCanonicalId.trim()),
    imageUrl: readSummaryImageUrl(input.summary),
    instrumentType: input.summary.instrumentType,
    issuedOnchainAssetCount: input.summary.issuedOnchainAssetCount,
    issuerProductCount: input.summary.issuerProductCount,
    marketCapRank: readPositiveInteger(input.summary.metadata.marketCapRank),
    marketCapUsd: parseOptionalNumber(input.summary.marketCapUsd),
    metadata: input.summary.metadata,
    name:
      nonEmptyString(input.summary.name) ?? canonicalSymbol ?? "Unknown asset",
    priceChangePercent1h: null,
    priceChangePercent24h:
      isUnpricedTradFiPlaceholder && parsedPriceChangePercent24h === 0
        ? null
        : parsedPriceChangePercent24h,
    priceUsd: isUnpricedTradFiPlaceholder ? null : parsedPriceUsd,
    primaryExchange: input.summary.primaryExchange,
    sicDescription: input.summary.sicDescription,
    sectorClassification: input.summary.sectorClassification ?? null,
    supportedIssuedOnchainAssetCount:
      input.summary.supportedIssuedOnchainAssetCount,
    supportedIssuerProductCount: input.summary.supportedIssuerProductCount,
    symbol: canonicalSymbol ?? "UNKNOWN",
    streamSymbolUnique: input.summary.streamSymbolUnique ?? null,
    tokenRepresentationCount: input.summary.tokenRepresentationCount,
    totalValueLockedUsd: null,
    totalVolumeUsd: null,
    tradingStatus: input.summary.tradingStatus,
    updatedAt: input.updatedAt,
  };
}

function isEquityOrFundAssetClass(value: string | null): boolean {
  const normalized = value?.trim().toLowerCase();
  return normalized === "equity" || normalized === "fund";
}

export function normalizeAssetUniverseSymbols(
  symbols: readonly string[],
): string[] {
  return [
    ...new Set(symbols.flatMap((symbol) => normalizeSymbol(symbol) ?? [])),
  ].sort((left, right) => left.localeCompare(right));
}

function normalizeAssetUniverseAssetId(assetId: string): string | null {
  const [normalizedAssetId] = normalizeAssetDomainAssetIds([
    tryAssetIdLikeToLegacyAssetId(assetId) ?? assetId,
  ]);

  return normalizedAssetId ?? null;
}

function normalizeRelationshipType(
  value: RwaTokenSubjectRelationType,
): RwaTokenSubjectRelationType {
  return TOKEN_SUBJECT_RELATION_TYPES.has(value)
    ? value
    : "issuer_representation";
}

export function readAssetUniverseGroupCoingeckoId(
  group:
    | {
        readonly underlying: Pick<
          AssetUniverseCanonicalGroup["underlying"],
          "metadata"
        >;
        readonly marketDataIdentifiers: readonly Pick<
          AssetUniverseCanonicalGroup["marketDataIdentifiers"][number],
          "provider" | "providerAssetId"
        >[];
        readonly issuedProducts: readonly Pick<
          AssetUniverseCanonicalGroup["issuedProducts"][number],
          "metadata" | "source" | "sourceProductId"
        >[];
      }
    | null
    | undefined,
): string | null {
  if (!group) {
    return null;
  }

  const identifierCoinId =
    group.marketDataIdentifiers.find(
      (identifier) => identifier.provider.toLowerCase() === "coingecko",
    )?.providerAssetId ?? null;

  return firstNormalizedCoingeckoId(
    readMetadataString(group.underlying.metadata.coingeckoId),
    identifierCoinId,
    ...group.issuedProducts.flatMap((product) => [
      readMetadataString(product.metadata.canonicalCoingeckoId),
      readMetadataString(product.metadata.coingeckoId),
      product.source.toLowerCase() === "coingecko"
        ? product.sourceProductId
        : null,
    ]),
  );
}

export function readAssetUniverseSummaryCoingeckoId(
  summary: AssetUniverseCanonicalSummaryLike | null | undefined,
): string | null {
  if (!summary) {
    return null;
  }

  return firstNormalizedCoingeckoId(
    readMetadataString(summary.metadata.coingeckoId),
  );
}

function firstNormalizedCoingeckoId(
  ...values: ReadonlyArray<string | null | undefined>
): string | null {
  for (const value of values) {
    const normalized = normalizeCoingeckoId(value);
    if (normalized) {
      return normalized;
    }
  }

  return null;
}

function normalizeCoingeckoId(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }

  return trimmed.replace(/^coingecko:/i, "").toLowerCase();
}

function normalizeSymbol(symbol: string | null | undefined): string | null {
  const trimmed = symbol?.trim();
  return trimmed ? trimmed.toUpperCase() : null;
}

function nonEmptyString(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function parseOptionalNumber(
  value: string | number | null | undefined,
): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function readPositiveInteger(value: unknown): number | null {
  const parsed = parseOptionalNumber(
    typeof value === "string" || typeof value === "number" ? value : null,
  );
  if (parsed === null || parsed < 1) {
    return null;
  }

  return Math.trunc(parsed);
}

function readMetadataImageUrl(
  metadata: Record<string, unknown>,
): string | null {
  return (
    readMetadataString(metadata.imageUrl) ??
    readMetadataString(metadata.imageLargeUrl) ??
    readMetadataString(metadata.imageSmallUrl) ??
    readMetadataString(metadata.imageThumbUrl) ??
    null
  );
}

function readSummaryImageUrl(
  summary: AssetUniverseCanonicalSummaryLike,
): string | null {
  return (
    (typeof summary.imageUrl === "string" && summary.imageUrl.trim().length > 0
      ? summary.imageUrl
      : null) ?? readMetadataImageUrl(summary.metadata)
  );
}

function readMetadataString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function symbolLinkSourceRank(
  source: AssetUniverseSymbolLinkModel["source"],
): number {
  switch (source) {
    case "canonical":
      return 0;
    case "product":
      return 1;
    case "route":
      return 2;
  }
}
